import { isEmpty } from 'lodash';
import { getLogger } from 'loglevel';
import { convertTimeToUserTZ, getUserTimeFormat } from '../../../../utilities/DateTimeUtilities';
import { DEFAULT_INVALID_VALUE } from '../../../../utilities/FormattingUtilities';
import { DAILY, INTERVAL, INTERVAL_TYPE_FORMAT, WINDOW } from '../ServiceTestsScheduleConstants';

const log = getLogger('Schedule Formatters');

function convertScheduleTimeToUserTZ(time, fromTz, userTimeFormat) {
    try {
        return convertTimeToUserTZ(time, fromTz, userTimeFormat);
    } catch (error) {
        log.warn('Unable to convert schedule time to user timezone', error);
        return INTERVAL_TYPE_FORMAT;
    }
}

/**
 * Formats schedule type for display
 * Returns display-ready schedule type string (e.g., "Daily", "Window", "Continuous")
 *
 * @param {Object} scheduling - Scheduling object with type/typeFormat properties
 * @param {string} scheduling.type - Schedule type (lowercase: 'daily', 'window', 'interval')
 * @param {string} scheduling.typeFormat - Display-ready schedule type (e.g., 'Daily', 'Window')
 * @returns {string} Formatted schedule type or empty string
 */
export function formatScheduleType(scheduling) {
    if (!scheduling || isEmpty(scheduling)) {
        return DEFAULT_INVALID_VALUE;
    }
    return scheduling.typeFormat || scheduling.type || DEFAULT_INVALID_VALUE;
}

/**
 * Formats test interval for display with hours/minutes suffix
 * Daily schedules return empty string (interval not applicable)
 * Other schedules format as "Xh Ym" (e.g., "5h 30m", "2h", "45m")
 *
 * @param {Object} scheduling - Scheduling object with interval property
 * @param {number} scheduling.interval - Interval in seconds
 * @param {string} scheduling.type - Schedule type (used to detect Daily schedule)
 * @param {string} scheduling.typeFormat - Alternative schedule type property
 * @returns {string} Formatted interval string or empty string
 */
export function formatTestInterval(scheduling) {
    if (!scheduling || isEmpty(scheduling)) {
        return DEFAULT_INVALID_VALUE;
    }

    // Daily schedule doesn't show interval
    const scheduleType = (scheduling.type || scheduling.typeFormat)?.toLowerCase();
    if (scheduleType === DAILY) {
        return DEFAULT_INVALID_VALUE;
    }

    // Format interval as hours/minutes (e.g., "5h 30m")
    const intervalInSeconds = scheduling.interval;
    if (!intervalInSeconds) {
        return DEFAULT_INVALID_VALUE;
    }

    const hours = Math.floor(intervalInSeconds / 3600);
    const minutes = Math.floor((intervalInSeconds % 3600) / 60);

    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);

    return parts.join(' ') || DEFAULT_INVALID_VALUE;
}

/**
 * Formats schedule time for display in user's timezone
 * Handles Daily schedule (single time) and Window schedule (time range)
 * Interval schedule returns empty string (time not applicable)
 *
 * @param {Object} scheduling - Scheduling object with time properties
 * @param {string} scheduling.type - Schedule type ('daily', 'window', 'interval')
 * @param {string} scheduling.typeFormat - Alternative schedule type property
 * @param {string} scheduling.start - Start time in HH:mm format
 * @param {string} scheduling.end - End time in HH:mm format (for Window schedule)
 * @param {string} scheduling.timezone - IANA timezone (e.g., 'America/New_York')
 * @returns {string} Formatted time or time range in user's timezone, or empty string
 */
export function formatScheduleTime(scheduling) {
    if (!scheduling || isEmpty(scheduling)) {
        return DEFAULT_INVALID_VALUE;
    }

    const scheduleType = (scheduling.type || scheduling.typeFormat)?.toLowerCase();

    // Interval schedule doesn't show time
    if (scheduleType === INTERVAL) {
        return DEFAULT_INVALID_VALUE;
    }

    const fromTz = scheduling.timezone;
    const userTimeFormat = getUserTimeFormat();

    // Daily schedule: show single start time
    if (scheduleType === DAILY) {
        const startTime = scheduling.start;
        if (!startTime) {
            return DEFAULT_INVALID_VALUE;
        }
        return convertScheduleTimeToUserTZ(startTime, fromTz, userTimeFormat);
    }

    // Window schedule: show time range "start - end"
    if (scheduleType === WINDOW) {
        const startTime = scheduling.start;
        const endTime = scheduling.end;
        if (!startTime || !endTime) {
            return DEFAULT_INVALID_VALUE;
        }
        const start = convertScheduleTimeToUserTZ(startTime, fromTz, userTimeFormat);
        const end = convertScheduleTimeToUserTZ(endTime, fromTz, userTimeFormat);
        if (start === DEFAULT_INVALID_VALUE || end === DEFAULT_INVALID_VALUE) {
            return DEFAULT_INVALID_VALUE;
        }
        return `${start} - ${end}`;
    }

    // Unknown schedule type
    return DEFAULT_INVALID_VALUE;
}

/**
 * Adapter type mapping for display
 */
const ADAPTER_TYPE_MAP = {
    default: 'Wired',
    wired: 'Wired',
    wireless: 'Wireless',
    multi: 'Wireless',
};

/**
 * Formats adapter type for display
 * Maps adapter type keys to display-ready labels
 * Defaults to 'Wired' when adapter type is empty or invalid
 *
 * @param {string} adapterType - Adapter type string ('default', 'wired', 'wireless', 'multi')
 * @returns {string} Formatted adapter type ('Wired' or 'Wireless')
 *
 * @example
 * formatAdapterType('default') => 'Wired'
 * formatAdapterType('wired') => 'Wired'
 * formatAdapterType('wireless') => 'Wireless'
 * formatAdapterType('multi') => 'Wireless'
 * formatAdapterType('') => 'Wired' (default)
 * formatAdapterType(null) => 'Wired' (default)
 */
export function formatAdapterType(adapterType) {
    if (!adapterType || isEmpty(adapterType)) {
        return ADAPTER_TYPE_MAP.wired;
    }
    return ADAPTER_TYPE_MAP[adapterType] || ADAPTER_TYPE_MAP.wired;
}
